import { useEffect, useState } from "react";
import peyotlHero from "./assets/peyotl-hero.png";
import "./App.css";

type Language = "en" | "es";

type PageContent = {
  htmlLang: string;
  navLabel: string;
  languageLabel: string;
  eyebrow: string;
  title: string;
  intro: string;
  purposeLabel: string;
  purposeTitle: string;
  purposeText: string;
  focusLabel: string;
  focusTitle: string;
  focusAreas: { title: string; description: string }[];
};

const content: Record<Language, PageContent> = {
  en: {
    htmlLang: "en",
    navLabel: "Primary",
    languageLabel: "Language selector",
    eyebrow: "Professional technology portal",
    title: "Technology ideas for practical solutions.",
    intro:
      "A professional space focused on exploring technology ideas, practical approaches, and open collaboration around real needs across business, social, and open-source contexts.",
    purposeLabel: "Purpose",
    purposeTitle: "Connecting technical knowledge, innovation, and execution.",
    purposeText:
      "The goal of peyotl.mx is to transform ideas into useful digital solutions with local and global relevance.",
    focusLabel: "Focus areas",
    focusTitle: "Ideas, research, social impact, and collaboration.",
    focusAreas: [
      {
        title: "Technology ideas",
        description: "Practical concepts that turn emerging technology into useful solutions.",
      },
      {
        title: "Research and experimentation",
        description: "Focused exploration, prototypes, and learning through real-world testing.",
      },
      {
        title: "Business and community challenges",
        description: "Digital approaches shaped around concrete organizational and local needs.",
      },
      {
        title: "Social impact initiatives",
        description: "Technology projects designed to create meaningful and measurable benefits.",
      },
      {
        title: "Open-source collaboration",
        description: "Shared tools and knowledge built transparently with the wider community.",
      },
      {
        title: "Local and global context",
        description: "Solutions grounded in local realities and informed by global perspectives.",
      },
    ],
  },
  es: {
    htmlLang: "es",
    navLabel: "Principal",
    languageLabel: "Selector de idioma",
    eyebrow: "Portal profesional de tecnología",
    title: "Ideas tecnológicas para soluciones prácticas.",
    intro:
      "Un espacio profesional enfocado en explorar ideas tecnológicas, enfoques prácticos y colaboración abierta alrededor de necesidades reales en contextos empresariales, sociales y de código abierto.",
    purposeLabel: "Propósito",
    purposeTitle: "Conectar conocimiento técnico, innovación y ejecución.",
    purposeText:
      "El objetivo de peyotl.mx es transformar ideas en soluciones digitales útiles con relevancia local y global.",
    focusLabel: "Áreas de enfoque",
    focusTitle: "Ideas, investigación, impacto social y colaboración.",
    focusAreas: [
      {
        title: "Ideas tecnológicas",
        description: "Conceptos prácticos que convierten tecnología emergente en soluciones útiles.",
      },
      {
        title: "Investigación y experimentación",
        description: "Exploración enfocada, prototipos y aprendizaje mediante pruebas reales.",
      },
      {
        title: "Retos empresariales y comunitarios",
        description: "Enfoques digitales creados para necesidades organizacionales y locales concretas.",
      },
      {
        title: "Iniciativas de impacto social",
        description: "Proyectos tecnológicos diseñados para generar beneficios significativos y medibles.",
      },
      {
        title: "Colaboración de código abierto",
        description: "Herramientas y conocimiento compartidos de forma transparente con la comunidad.",
      },
      {
        title: "Contexto local y global",
        description: "Soluciones basadas en realidades locales e informadas por perspectivas globales.",
      },
    ],
  },
};

function getBrowserLanguage(): Language {
  const browserLanguages =
    typeof navigator === "undefined"
      ? []
      : navigator.languages?.length
      ? navigator.languages
      : [navigator.language];

  const preferredLanguage = browserLanguages.find((browserLanguage) => {
    const baseLanguage = browserLanguage.toLowerCase().split("-")[0];
    return baseLanguage === "en" || baseLanguage === "es";
  });

  return preferredLanguage?.toLowerCase().split("-")[0] === "es" ? "es" : "en";
}

function PeyotlMark() {
  return (
    <svg
      className="brand-mark"
      viewBox="108 618 90 58"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
        <path d="M -0.00005 -0.0009625 L -0.00005 -25.415025 C -0.00005 -25.415025 2.910106 -52.309556 26.730419 -52.309556 L 44.714794 -52.309556 C 44.714794 -52.309556 45.68745 -10.391588 -0.00005 -0.0009625 Z M -0.00005 -0.0009625" transform="matrix(1, 0, 0, -1, 116.1563, 644.2256)" />
        <path d="M -0.0019125 -0.00194375 C 6.267619 10.013681 17.216837 19.888681 36.337931 24.236337 L 36.337931 -1.177725 C 36.337931 -1.177725 34.814494 -28.072256 9.607462 -28.072256" transform="matrix(1, 0, 0, -1, 152.4941, 668.4629)" />
        <path d="M -0.0019375 -0.00004375 C -0.0019375 -0.00004375 16.037125 8.355425 18.951188 7.660112 L 18.951188 -11.199263" transform="matrix(1, 0, 0, -1, 164.8535, 633.0273)" />
        <path d="M 0.0019125 -0.00004375 C 0.0019125 -0.00004375 -16.03715 8.355425 -18.951212 7.660112 L -18.951212 -11.199263" transform="matrix(1, 0, 0, -1, 140.2559, 633.0273)" />
        <path d="M -0.00194375 -0.00001875 C 0.0293063 8.640606 -4.294912 18.124981 -16.009756 27.999981 L -18.048819 29.851544 L -20.091787 27.999981 C -31.658194 18.246075 -36.021475 8.871075 -36.0996 0.320294" transform="matrix(1, 0, 0, -1, 170.3496, 651.2617)" />
      </g>
      <g fill="currentColor">
        <circle cx="152.621" cy="643.43" r="5.84" />
        <circle cx="183.805" cy="625.324" r="3.58" />
        <circle cx="121.301" cy="625.324" r="3.58" />
        <circle cx="116.156" cy="644.43" r="3.58" />
        <circle cx="188.832" cy="644.43" r="3.58" />
      </g>
    </svg>
  );
}

function App() {
  const [language, setLanguage] = useState<Language>(getBrowserLanguage);
  const copy = content[language];

  useEffect(() => {
    document.documentElement.lang = copy.htmlLang;
  }, [copy.htmlLang]);

  return (
    <main className="portal" lang={copy.htmlLang}>
      <section className="hero" aria-labelledby="page-title">
        <nav className="topbar" aria-label={copy.navLabel}>
          <span className="brand">
            <PeyotlMark />
            <span>peyotl.mx</span>
          </span>
          <div className="language-switch" aria-label={copy.languageLabel}>
            <button
              className={language === "en" ? "active" : ""}
              type="button"
              aria-pressed={language === "en"}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
            <button
              className={language === "es" ? "active" : ""}
              type="button"
              aria-pressed={language === "es"}
              onClick={() => setLanguage("es")}
            >
              ES
            </button>
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-copy">
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1 id="page-title">{copy.title}</h1>
            <p className="intro">{copy.intro}</p>
          </div>

          <figure className="hero-visual" aria-hidden="true">
            <img src={peyotlHero} alt="" />
          </figure>
        </div>
      </section>

      <section className="purpose" aria-label={copy.purposeLabel}>
        <div>
          <p className="section-label">{copy.purposeLabel}</p>
          <h2>{copy.purposeTitle}</h2>
        </div>
        <p>{copy.purposeText}</p>
      </section>

      <section className="focus" aria-labelledby="focus-title">
        <p className="section-label">{copy.focusLabel}</p>
        <h2 id="focus-title">{copy.focusTitle}</h2>
        <div className="focus-grid">
          {copy.focusAreas.map((area) => (
            <article className="focus-item" key={area.title}>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default App;
